import { CredentialStatus } from '@seek/ca-myprofile-utils';

import { getConfirmedRoleDataFromCache } from 'src/components/Profile/CareerHistory/apolloCache.optimistic';
import { getUnconfirmedRolesFromCache } from 'src/components/Profile/CareerHistory/data';
import {
  CreateResumeActions,
  CreateStep,
} from 'src/components/Profile/CreateResume/reducer';
import {
  getConfirmedQualificationsFromCache,
  getUnconfirmedQualificationsFromCache,
} from 'src/components/Profile/Education/data';
import { getLicences } from 'src/components/Profile/Licences/licences.graphql';
import type { GraphqlLicence } from 'src/components/Profile/Licences/types';
import { PROFILE_INSIGHTS_LOADED } from 'src/components/Profile/ProfileInsights/actionCreators';
import {
  Actions as ProfileVisibilityActions,
  type ProfileVisibilityAction,
} from 'src/components/Profile/ProfileVisibility/actions';
import { PROFILE_RESUME_DEFAULT_WITH_ALERT_LOADED } from 'src/components/Profile/Resume/EditResume/ManageResumes/ResumeDetailItem/actionCreators';
import { getSuggestedSkillsFromCache } from 'src/components/Profile/Skills/data';
import {
  PROFILE_SUGGESTIONS_CTA_PRESSED,
  PROFILE_SUGGESTIONS_LOADED,
} from 'src/components/Profile/Suggestions/actionCreators';
import { getCandidateIdFromCache } from 'src/components/Profile/data';
import { ProfileActions } from 'src/components/Profile/reducer';
import {
  EDUCATION,
  PERSONAL_DETAILS,
  SUMMARY,
  CAREER_HISTORY,
  RESUME,
  SKILLS,
  CREATE_RESUME_PAGE,
  LICENCES,
  LANGUAGES,
  AVAILABILITY,
  WORKTYPES,
  PREFERRED_LOCATIONS,
  RIGHT_TO_WORK,
  SALARY,
  CLASSIFICATION,
  APPROACHABILITY,
} from 'src/components/Router';
import {
  UPDATE_ROUTE,
  UPDATE_PAGE,
  type UpdatePageParams,
} from 'src/components/Router/reducer';
import { EDIT } from 'src/consts';
import { getUserDetailsFromCache } from 'src/handlers/analytics';
import {
  type createAnalytics,
  getProfileSharedStatus,
} from 'src/modules/analytics';
import type { ProfileState } from 'src/modules/analytics/reducers/profile';
import type {
  AnalyticsActionOrigin,
  AnalyticsCurrentPageSectionState,
  AnalyticsLinkEvent,
  AnalyticsProfileVisibilityLevel,
  AnalyticsLinkPosition,
  AnalyticsHeaderFooterLinkProps,
  AnalyticsUpdateType,
  AnalyticsCurrentPageSection,
  AnalyticsCurrentPageSubsection,
  AnalyticsProfileSuggestionsProps,
  AnalyticsResumeAlertShownType,
  AnalyticsResumeAlertShownTone,
  AnalyticsResumeAlertPosition,
  AnalyticsResumeAlertShownMessage,
  AnalyticsResumeAlertTitle,
} from 'src/modules/analytics/types';
import type {
  CardOpenProps,
  CustomProps,
} from 'src/modules/analytics/types/cardOpened';
import { Actions as YearsOfExperienceActions } from 'src/modules/analytics/yearsOfExperience/actionCreators';
import { getDataFromCache } from 'src/modules/apollo/apolloCache';
import type { AppConfig } from 'src/modules/appConfig/types';
import {
  COMPANY_REVIEWS_LINK_CLICKED,
  HEADER_FOOTER_LINK_CLICKED,
  TRACKED_LINK_CLICKED,
} from 'src/reducers/location';
import type {
  Action,
  Mode,
  ReduxInjections,
  Card,
  ApiClient,
  Viewer,
} from 'src/types';
import {
  GetApproachabilityDocument,
  GetProfileInsightsDocument,
  GetProfileVisibilityDocument,
  GetShareableProfileSettingsDocument,
  GetVerificationsDocument,
} from 'src/types/graphql';

import {
  instrumentAnalytics,
  applySuccessItemCheck,
} from './analytics/analyticsMiddlewareUtils';
import {
  calculateLicenceAnalytics,
  calculateQualificationAnalytics,
  calculateVerificationsAnalytics,
  getVerificationStatus,
} from './utils';

type AnalyticsMiddlewareAction =
  | (Action<typeof UPDATE_PAGE> & {
      payload: UpdatePageParams;
    })
  | (Action<typeof UPDATE_ROUTE> & {
      payload: {
        mode: Mode;
        to: Card;
        analytics: {
          origin?: AnalyticsActionOrigin;
          disabled?: boolean;
          action?: string;
          currentPageSectionState: AnalyticsCurrentPageSectionState;
          currentPageSubsection?: AnalyticsCurrentPageSubsection;
        };
        params: { id?: string };
      };
    })
  | (Action<CreateResumeActions.UPDATE_CREATE_STEP> & {
      payload: { from: CreateStep; to: CreateStep };
    })
  | Extract<
      ProfileVisibilityAction,
      Action<ProfileVisibilityActions.PROFILE_VISIBILITY_UPDATE_START>
    >
  | (Action<typeof COMPANY_REVIEWS_LINK_CLICKED> & {
      payload: {
        companyName: string;
      };
    })
  | (Action<typeof TRACKED_LINK_CLICKED> & {
      payload: {
        analytics: {
          linkName: AnalyticsLinkEvent;
          thirdPartyLink: boolean;
          linkPosition: AnalyticsLinkPosition;
        };
        type: string;
      };
    })
  | (Action<typeof HEADER_FOOTER_LINK_CLICKED> & {
      payload: AnalyticsHeaderFooterLinkProps;
    })
  | Action<typeof PROFILE_INSIGHTS_LOADED>
  | (Action<typeof PROFILE_SUGGESTIONS_LOADED> & {
      payload: AnalyticsProfileSuggestionsProps;
    })
  | (Action<typeof PROFILE_SUGGESTIONS_CTA_PRESSED> & {
      payload: AnalyticsProfileSuggestionsProps;
    })
  | Action<CreateResumeActions.CREATE_RESUME_RENDERED>
  | Action<ProfileActions.PROFILE_RENDERED>
  | Action<ProfileVisibilityActions.PROFILE_VISIBILITY_OPENED>
  | (Action<YearsOfExperienceActions.YEARS_OF_EXPERIENCE_EDIT_LOADED> & {
      payload: {
        currentPageSectionState: AnalyticsCurrentPageSectionState;
      };
    })
  | (Action<typeof PROFILE_RESUME_DEFAULT_WITH_ALERT_LOADED> & {
      payload: {
        alertShownType: AnalyticsResumeAlertShownType;
        alertShownTone: AnalyticsResumeAlertShownTone;
        alertShownPosition: AnalyticsResumeAlertPosition;
        alertShownTitle: AnalyticsResumeAlertTitle;
        alertShownMessage: AnalyticsResumeAlertShownMessage;
      };
    })
  | (Action<YearsOfExperienceActions.YEARS_OF_EXPERIENCE_UPDATE> & {
      payload: {
        actionName: AnalyticsUpdateType;
        userIsNewToWorkforce: boolean;
      };
    });

export type Analytics = ReturnType<typeof createAnalytics>;

export type AnalyticsEditLoadedType = Pick<
  CardOpenProps,
  'currentPageSectionState' | 'customProps' | 'currentPageSubsection'
>;

export const createAnalyticsMiddleware = (
  analytics: Analytics,
  { apolloClient }: ApiClient,
  { language: languageCode, showFeature, zone, isUnifiedSite }: AppConfig,
) =>
  instrumentAnalytics(analytics) &&
  (({ getState }: ReduxInjections) =>
    (next: (action: AnalyticsMiddlewareAction) => void) =>
    (action: AnalyticsMiddlewareAction) => {
      const {
        profileVisibilityUpdate,
        pageUpdated,
        profileLoaded,
        resumeAlertViewed,
        profileInsightsAnalyticsTracker,
        profileSuggestionsAnalyticsTracker,
        createResumeLoaded,
        careerHistoryEditLoaded,
        educationEditLoaded,
        languagesEditLoaded,
        licencesEditLoaded,
        yearsOfExperienceEditLoaded,
        unconfirmedDataEditLoadedFromApplySuccess,
        personalDetailsEditLoaded,
        personalSummaryEditLoaded,
        profileVisibilityEditLoaded,
        resumeEditLoaded,
        nextRoleEditLoaded,
        skillsEditLoaded,
        profileCreateResumeButtonClicked,
        createResumeButtonClicked,
        getStartedButtonClicked,
        linkClicked,
        headerFooterLinkClicked,
        companyReviewsClicked,
        setProfileActionOriginInState,
      } = analytics;

      const analyticsMethodsMappedToRoutes: Record<
        string,
        (_: AnalyticsEditLoadedType) => void
      > = {
        [PERSONAL_DETAILS]: personalDetailsEditLoaded,
        [SUMMARY]: personalSummaryEditLoaded,
        [CAREER_HISTORY]: careerHistoryEditLoaded,
        [EDUCATION]: educationEditLoaded,
        [LICENCES]: licencesEditLoaded,
        [RESUME]: resumeEditLoaded,
        [SKILLS]: skillsEditLoaded,
        [LANGUAGES]: languagesEditLoaded,
        [AVAILABILITY]: nextRoleEditLoaded,
        [WORKTYPES]: nextRoleEditLoaded,
        [PREFERRED_LOCATIONS]: nextRoleEditLoaded,
        [RIGHT_TO_WORK]: nextRoleEditLoaded,
        [SALARY]: nextRoleEditLoaded,
        [CLASSIFICATION]: nextRoleEditLoaded,
        [APPROACHABILITY]: nextRoleEditLoaded,
      };

      const analyticsCurrentPageSectionMapper: Record<
        string,
        AnalyticsCurrentPageSection
      > = {
        [EDUCATION]: 'education',
        [CAREER_HISTORY]: 'career history',
      };

      const state = getState();
      const {
        router: { page: currentPage },
      } = state;

      // PROFILE_PAGE;

      const getLicenceData = () =>
        apolloClient.readQuery<Viewer<{ licences: GraphqlLicence[] }>>({
          query: getLicences,
          variables: { languageCode: 'en' },
        });

      const getProfileVisibilityLevelData = () =>
        getDataFromCache({
          cache: apolloClient.cache,
          query: GetProfileVisibilityDocument,
          variables: { languageCode: 'en' },
        });

      const getProfileApproachabilityData = () =>
        getDataFromCache({
          cache: apolloClient.cache,
          query: GetApproachabilityDocument,
        });

      const getShareableProfileSettingsData = () =>
        getDataFromCache({
          cache: apolloClient.cache,
          query: GetShareableProfileSettingsDocument,
          variables: {
            languageCode: 'en',
            zone,
          },
        });

      const getProfileInsightsData = () =>
        getDataFromCache({
          cache: apolloClient.cache,
          query: GetProfileInsightsDocument,
          variables: {
            zone,
            languageCode: 'en',
            timezone: 'Australia/Melbourne',
          },
        });

      const getGlobalProfileStateTrackingData = (): ProfileState => {
        const candidateId = getCandidateIdFromCache(apolloClient.cache);

        const profileVisibilityLevelData = getProfileVisibilityLevelData();

        const level =
          profileVisibilityLevelData?.viewer?.profileVisibility?.level;

        const profileVisibilityLevel = (
          level ? level.toLowerCase() : 'not set'
        ) as AnalyticsProfileVisibilityLevel;

        const profileApproachabilityData = getProfileApproachabilityData();

        const approachable =
          profileApproachabilityData?.viewer?.approachability?.approachable ??
          true;

        const profileApproachabilityStatus = approachable ? 'on' : 'off';

        const shareableProfileSettingsData = getShareableProfileSettingsData();

        const isShareableProfileActive =
          shareableProfileSettingsData?.viewer?.shareableProfileSettings
            ?.active || false;

        const profileSharedStatus = getProfileSharedStatus({
          isShareableProfileCreated: isShareableProfileActive,
          isShareableProfileActive,
        });

        const profileInsights =
          getProfileInsightsData()?.viewer?.profileInsights;

        return {
          profileVisibilityLevel,
          profileApproachabilityStatus,
          profileSharedStatus,
          candidateId: candidateId ?? undefined,
          profileViewedCount: profileInsights?.viewCount ?? 0,
          profileSearchAppearanceCount:
            profileInsights?.searchImpressionCount ?? 0,
        };
      };

      switch (action.type) {
        case CreateResumeActions.CREATE_RESUME_RENDERED: {
          const { _id, trackingId, externalId } = getUserDetailsFromCache(
            apolloClient.cache,
          );
          pageUpdated({ currentPage });

          createResumeLoaded(
            {
              eventName: 'create_resume_displayed',
              currentPage: 'create resume',
            },
            {
              trackingId,
              _id,
              externalId: externalId || undefined,
              isUnifiedSite,
            },
          );

          break;
        }

        case ProfileActions.PROFILE_RENDERED: {
          const { _id, trackingId, externalId } = getUserDetailsFromCache(
            apolloClient.cache,
          );

          const licenceData = getLicenceData();
          const licences = licenceData?.viewer?.licences || [];
          const { unconfirmedRoles } = getUnconfirmedRolesFromCache(
            apolloClient,
            showFeature('resume-parsing'),
          );

          const { unconfirmedQualifications } =
            getUnconfirmedQualificationsFromCache({
              apolloClient,
              languageCode,
              zone,
              isUnifiedSite,
            });
          const { confirmedQualifications } =
            getConfirmedQualificationsFromCache({
              ...apolloClient,
              languageCode,
              showQualificationLevel2: showFeature('qualification-level-major'),
              zone,
              isUnifiedSite,
            });

          const getProfileVerificationsData = () =>
            getDataFromCache({
              cache: apolloClient.cache,
              query: GetVerificationsDocument,
              variables: {
                zone,
                locale: 'en-AU',
              },
            });

          const verifications =
            getProfileVerificationsData()?.viewer?.profileVerifications;

          const workHistory = getConfirmedRoleDataFromCache({
            cache: apolloClient.cache,
          });
          const profileCareerHistoryCount = workHistory?.length;

          const isResumeParsed =
            unconfirmedRoles.length > 0 || unconfirmedQualifications.length > 0;

          pageUpdated({ currentPage });

          profileLoaded(getGlobalProfileStateTrackingData(), {
            trackingId,
            _id,
            externalId: externalId || undefined,
            isResumeParsed,
            isUnifiedSite,
            ...calculateLicenceAnalytics(licences),
            ...(confirmedQualifications.length > 0
              ? calculateQualificationAnalytics(confirmedQualifications)
              : {}),
            ...calculateVerificationsAnalytics(
              verifications?.verified,
              verifications?.verifiable,
            ),
            profileCareerHistoryCount,
          });
          break;
        }

        case PROFILE_INSIGHTS_LOADED: {
          profileInsightsAnalyticsTracker({
            eventName: 'profile_insights_widget_loaded',
            profileState: getGlobalProfileStateTrackingData(),
          });

          break;
        }

        case PROFILE_SUGGESTIONS_LOADED: {
          const props = action.payload;
          profileSuggestionsAnalyticsTracker({
            eventName: 'nudge_viewed',
            profileState: getGlobalProfileStateTrackingData(),
            props,
          });

          break;
        }
        case PROFILE_SUGGESTIONS_CTA_PRESSED: {
          const props = action.payload;
          profileSuggestionsAnalyticsTracker({
            eventName: 'nudge_pressed',
            props,
          });

          break;
        }

        case PROFILE_RESUME_DEFAULT_WITH_ALERT_LOADED: {
          const props = action.payload;
          resumeAlertViewed({
            ...props,
            profileState: getGlobalProfileStateTrackingData(),
          });

          break;
        }

        case UPDATE_PAGE: {
          const { page, analytics: { action: actionName = '' } = {} } =
            action.payload;
          if (
            page === CREATE_RESUME_PAGE &&
            actionName === 'profile-create-resume-get-started-pressed'
          ) {
            profileCreateResumeButtonClicked();
          }
          break;
        }

        case UPDATE_ROUTE: {
          const {
            mode,
            to,
            params: { id: currentRouteId = '' } = {},
            analytics: {
              origin,
              action: actionName,
              disabled,
              currentPageSectionState,
              currentPageSubsection,
            } = {},
          } = action.payload;

          if (disabled === true) {
            break;
          }

          setProfileActionOriginInState(origin);

          if (mode === EDIT && analyticsMethodsMappedToRoutes[to]) {
            const isApplySuccessEdit = applySuccessItemCheck(
              getState,
              currentRouteId,
            );

            const customProps: CustomProps = {};

            if (to === LICENCES) {
              const licenceData = getLicenceData();

              const licences: GraphqlLicence[] =
                licenceData?.viewer?.licences || [];
              const licence = licences.find((l) => l.id === currentRouteId);

              if (licence) {
                customProps.verificationStatus = getVerificationStatus(licence);
                customProps.isExpired =
                  licence.status === CredentialStatus.Expired;
              }
            }

            if (to === EDUCATION) {
              const { confirmedQualifications } =
                getConfirmedQualificationsFromCache({
                  ...apolloClient,
                  languageCode,
                  showQualificationLevel2: showFeature(
                    'qualification-level-major',
                  ),
                  zone,
                  isUnifiedSite,
                });

              const qualification = confirmedQualifications.find(
                (qual) => qual.id === currentRouteId,
              );

              if (qualification) {
                customProps.verificationStatus =
                  getVerificationStatus(qualification);
              }
            }

            if (to === 'skills') {
              const {
                router: { page },
              } = getState();

              const { suggestedSkills } = getSuggestedSkillsFromCache({
                apolloClient,
                zone,
                languageCode,
              });

              const hasSuggestedSkills = suggestedSkills.length > 0;
              const isSuggestedSkillsSectionVisible =
                page === 'profile-page' && hasSuggestedSkills;

              customProps.opportunityType = isSuggestedSkillsSectionVisible
                ? 'suggested skills'
                : undefined;
            }

            if (currentPageSectionState) {
              if (isApplySuccessEdit) {
                unconfirmedDataEditLoadedFromApplySuccess({
                  currentPageSection: analyticsCurrentPageSectionMapper[to],
                  currentPageSectionState,
                });
              } else {
                analyticsMethodsMappedToRoutes[to]({
                  currentPageSectionState,
                  customProps,
                  currentPageSubsection,
                });
              }
            }
          }

          if (actionName === 'create-resume-get-started-pressed') {
            getStartedButtonClicked();
          }
          break;
        }

        case ProfileVisibilityActions.PROFILE_VISIBILITY_OPENED: {
          profileVisibilityEditLoaded();
          break;
        }

        case CreateResumeActions.UPDATE_CREATE_STEP: {
          const { to: newCreateStep } = action.payload;
          if (newCreateStep === CreateStep.PROFILE_REVIEW) {
            createResumeButtonClicked();
          }
          break;
        }

        case ProfileVisibilityActions.PROFILE_VISIBILITY_UPDATE_START: {
          const { level } = action.payload;

          const profileVisibilityLevel =
            level.toLowerCase() as AnalyticsProfileVisibilityLevel;

          profileVisibilityUpdate(profileVisibilityLevel);
          break;
        }

        case COMPANY_REVIEWS_LINK_CLICKED: {
          const { companyName } = action.payload;

          companyReviewsClicked({
            linkNameEventName: 'company_reviews_pressed',
            currentPageSection: 'career history',
            companyName,
          });

          break;
        }

        case TRACKED_LINK_CLICKED: {
          const {
            analytics: { linkName, linkPosition, thirdPartyLink },
            type: linkType,
          } = action.payload;
          linkClicked({
            linkNameEventName: linkName,
            linkPosition,
            thirdPartyLink,
            type: linkType,
          });
          break;
        }

        case HEADER_FOOTER_LINK_CLICKED: {
          const { linkPosition, linkText, linkAction } = action.payload;

          headerFooterLinkClicked({
            linkNameEventName: 'navigation_pressed',
            linkPosition,
            linkText,
            linkAction,
          });
          break;
        }

        case YearsOfExperienceActions.YEARS_OF_EXPERIENCE_EDIT_LOADED: {
          const { currentPageSectionState } = action.payload;
          yearsOfExperienceEditLoaded({
            currentPageSectionState,
          });

          break;
        }

        default: {
          break;
        }
      }

      return next(action);
    });
